import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { faFolderImage } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@sixui/ui';
import { useTranslation } from 'react-i18next';
import { useQueries } from 'react-query';

import type {
  IFileDropZoneFileInfo,
  IFileDropZonePartialFileInfo,
  IFileDropZoneProps,
} from '~/components/molecules/FileDropZone';
import type {
  IApiAbstractFile,
  IApiFileUploadProgressEvent,
} from '~/lib/api/modules/Files';
import type { IApiGetPictureResponseBody } from '~/lib/api/modules/Pictures';
import type { IApiGetVideoResponseBody } from '~/lib/api/modules/Videos';
import { FileDropZone } from '~/components/molecules/FileDropZone';
import { FileGalleryDialog } from '~/components/organisms/FileGalleryDialog';
import { ITimeUnit } from '~/helpers/types';
import { useApi } from '~/lib/api';
import { IApiFileUploadStatus } from '~/lib/api/modules/Files';

export type { IFileDropZoneFileInfo as IFileDropZoneFile } from '~/components/molecules/FileDropZone';

// TODO: fix "An invalid form control with is not focusable" error when the file
// is not provided.

export type IFileUploaderFileType = 'picture' | 'video' | 'document';

export type IFileUploaderValue<
  TFileType extends IFileUploaderFileType = IFileUploaderFileType,
> = {
  type?: TFileType;
  id: string;
};

export type IFileUploadOnStatusChangeEvent = {
  id?: string;
  error?: Error;
  fileInfo?: IFileDropZoneFileInfo;
  status: IApiFileUploadStatus | undefined;
  type?: IFileUploaderFileType;
};

export type IFileUploaderOnStatusChange = (
  event: IFileUploadOnStatusChangeEvent,
) => void;

export type IFileUploaderProps = Omit<IFileDropZoneProps, 'files'> & {
  appId?: string;
  onChange?: (value: Array<IFileUploaderValue>) => void;
  onStatusChange?: IFileUploaderOnStatusChange;
  getPollingWaitTime?: (previousPollingWaitTime: number) => number;
  value?: Array<IFileUploaderValue>;
  acceptTypes?: Array<IFileUploaderFileType>;
};

const typeToMimeType = {
  picture: { 'image/*': [] },
  video: { 'video/*': [] },
  document: { 'application/pdf': ['.pdf'] },
};

const getFileTypeFromMimeType = (
  mimeType: string,
): IFileUploaderFileType | undefined => {
  if (mimeType.startsWith('image/')) {
    return 'picture';
  }

  if (mimeType.startsWith('video/')) {
    return 'video';
  }

  if (mimeType === 'application/pdf') {
    return 'document';
  }

  return undefined;
};

const DEFAULT_WAIT_TIME = 1 * ITimeUnit.SECOND;

export const FileUploader = forwardRef<HTMLInputElement, IFileUploaderProps>(
  function FileUploader(props, forwardedRef) {
    const {
      appId,
      onChange,
      onStatusChange = () => {},
      getPollingWaitTime = (previousPollingWaitTime: number) => {
        return previousPollingWaitTime * 1.3;
      },
      value,
      acceptTypes,
      ...fileDropZoneProps
    } = props;

    const api = useApi();
    const filesInfoRef = useRef<Array<IFileDropZonePartialFileInfo>>([]);
    const [filesInfo, setFilesInfo] = useState<
      Array<IFileDropZonePartialFileInfo>
    >([]);
    const [fileGalleryDialogOpened, setFileGalleryDialogOpened] =
      useState(false);
    const { t } = useTranslation();

    useEffect(() => {
      // This component is controlled, which means that the value prop is always
      // the source of truth. When the value prop changes, the component should
      // update the files info (that contains the updated file information and
      // status).
      filesInfoRef.current = [
        ...(value
          ? value.map(({ type, id }) => ({
              mimeType:
                type === 'picture'
                  ? 'image/*'
                  : type === 'video'
                    ? 'video/*'
                    : 'application/pdf',
              key: id,
              id: id,
              name: id,
              loading: false,
              canRetry: false,
              ...filesInfoRef.current.find(({ id: fileId }) => fileId === id),
            }))
          : []),
        ...filesInfoRef.current.filter(({ id }) => id === undefined),
      ];
      setFilesInfo(filesInfoRef.current);
    }, [value]);

    const getFileStatus = useCallback(
      async (
        fileId: string,
        documentType: IFileUploaderFileType,
      ): Promise<IApiFileUploadStatus | undefined> => {
        let document: IApiAbstractFile;

        if (documentType === 'document') {
          document = await api.documents.get(appId, fileId);
        } else if (documentType === 'picture') {
          document = await api.pictures.get(appId, fileId);
        } else {
          document = await api.videos.get(appId, fileId);
        }

        return document?.status;
      },
      [api, appId],
    );

    const pictureQueries = useQueries(
      value?.map(({ type, id }) => ({
        queryKey: [appId, 'picture', id],
        queryFn: () => api.pictures.get(appId, id),
        enabled: type === 'picture',
        onSuccess: (data: IApiGetPictureResponseBody) => {
          filesInfoRef.current = filesInfoRef.current
            .map((fileInfo) =>
              fileInfo.key === id
                ? {
                    ...fileInfo,
                    name: data.name,
                    thumbUrl: data.mediumUrl,
                    loading: false,
                  }
                : fileInfo,
            )
            .slice(-(fileDropZoneProps.maxFiles ?? 0));
          setFilesInfo(filesInfoRef.current);
        },
        onError: (error: Error) => {
          filesInfoRef.current = filesInfoRef.current
            .map((fileInfo) =>
              fileInfo.key === id
                ? {
                    ...fileInfo,
                    loading: false,
                    errorText: error.message,
                  }
                : fileInfo,
            )
            .slice(-(fileDropZoneProps.maxFiles ?? 0));
          setFilesInfo(filesInfoRef.current);
        },
      })) ?? [],
    );

    const videoQueries = useQueries(
      value?.map(({ type, id }) => ({
        queryKey: [appId, 'video', id],
        queryFn: () => api.videos.get(appId, id),
        enabled: type === 'video',
        onSuccess: (data: IApiGetVideoResponseBody) => {
          filesInfoRef.current = filesInfoRef.current
            .map((fileInfo) =>
              fileInfo.key === id
                ? {
                    ...fileInfo,
                    name: data.name,
                    thumbUrl: data.thumbUrl,
                    loading: false,
                  }
                : fileInfo,
            )
            .slice(-(fileDropZoneProps.maxFiles ?? 0));
          setFilesInfo(filesInfoRef.current);
        },
        onError: (error: Error) => {
          filesInfoRef.current = filesInfoRef.current
            .map((fileInfo) =>
              fileInfo.key === id
                ? {
                    ...fileInfo,
                    loading: false,
                    errorText: error.message,
                  }
                : fileInfo,
            )
            .slice(-(fileDropZoneProps.maxFiles ?? 0));
          setFilesInfo(filesInfoRef.current);
        },
      })) ?? [],
    );

    const loading =
      pictureQueries.some(({ isLoading }) => isLoading) ||
      videoQueries.some(({ isLoading }) => isLoading);
    const disabled = loading || fileDropZoneProps.disabled;

    const handleProgressEvent = useCallback(
      (key: string, progressEvent: IApiFileUploadProgressEvent): void => {
        filesInfoRef.current = filesInfoRef.current
          .map((fileInfo) =>
            fileInfo.key === key
              ? {
                  ...fileInfo,
                  loading: true,
                  progress: progressEvent.progress,
                }
              : fileInfo,
          )
          .slice(-(fileDropZoneProps.maxFiles ?? 0));
        setFilesInfo(filesInfoRef.current);
      },
      [fileDropZoneProps.maxFiles],
    );

    const uploadFiles = useCallback(
      (acceptedFilesInfo: Array<IFileDropZoneFileInfo>) => {
        filesInfoRef.current = filesInfoRef.current
          .map((fileInfo) =>
            acceptedFilesInfo.find(({ key }) => fileInfo.key === key)
              ? {
                  ...fileInfo,
                  loading: true,
                  errorText: undefined,
                }
              : fileInfo,
          )
          .slice(-(fileDropZoneProps.maxFiles ?? 0));
        setFilesInfo(filesInfoRef.current);

        void api.files
          .prepareForUpload(appId, {
            files: acceptedFilesInfo.map((fileInfo) => {
              onStatusChange({
                type: getFileTypeFromMimeType(fileInfo.mimeType),
                fileInfo,
                status: IApiFileUploadStatus.PREPARING_FILE,
              });
              return {
                name: fileInfo.name,
                type: fileInfo.mimeType,
                size: fileInfo.size,
              };
            }),
            metadata: {
              opts: JSON.stringify({ keepRatio: true }),
            },
          })
          .then((uploadSlots) => {
            // Update ID
            filesInfoRef.current = filesInfoRef.current
              .map((fileInfo) => {
                const index = acceptedFilesInfo.findIndex(
                  ({ key }) => fileInfo.key === key,
                );

                return index >= 0
                  ? {
                      ...fileInfo,
                      id: uploadSlots[index].id,
                    }
                  : fileInfo;
              })
              .slice(-(fileDropZoneProps.maxFiles ?? 0));
            setFilesInfo(filesInfoRef.current);

            const fileIds = uploadSlots.map(({ id }) => id);

            return Promise.all(
              acceptedFilesInfo.map((fileInfo, index) => {
                const abortController = fileInfo.abortController;

                if (abortController?.signal.aborted) {
                  return;
                }

                const fileType = getFileTypeFromMimeType(fileInfo.mimeType);
                const fileId = fileIds[index];
                if (fileType && fileId && fileInfo.file) {
                  onStatusChange({
                    type: fileType,
                    id: fileId,
                    status: IApiFileUploadStatus.UPLOADING,
                  });
                }

                return fileInfo.file
                  ? api.files.upload(
                      fileInfo.file,
                      uploadSlots[index].url,
                      (progressEvent) =>
                        handleProgressEvent(fileInfo.key, progressEvent),
                      abortController,
                    )
                  : undefined;
              }),
            ).then(() => fileIds);
          })
          .then(async (fileIds) => {
            /*
              Post upload processing is applied to each file. This step
              makes sure this processing is over before going to the next one.
            */
            const processedFilesIds = new Set<string>();

            await Promise.all(
              acceptedFilesInfo.map(async (fileInfo, index) => {
                const fileType = getFileTypeFromMimeType(fileInfo.mimeType);
                const fileId = fileIds[index];
                if (!fileType || !fileId) {
                  return;
                }
                // document stays in uploading status until post processing starts
                let previousStatus: IApiFileUploadStatus | undefined =
                  IApiFileUploadStatus.UPLOADING;
                let status: IApiFileUploadStatus | undefined =
                  IApiFileUploadStatus.UPLOADING;
                let waitTime = DEFAULT_WAIT_TIME;

                while (status !== IApiFileUploadStatus.READY) {
                  // user has canceled the upload
                  if (fileInfo.abortController?.signal.aborted) {
                    break;
                  }

                  status = await getFileStatus(fileId, fileType);

                  if (
                    status === IApiFileUploadStatus.ENCODING_ERROR ||
                    status === IApiFileUploadStatus.ENCODING_JOB_ERROR
                  ) {
                    const error = new Error(
                      'File upload post-processing failed',
                    );
                    onStatusChange({
                      type: fileType,
                      id: fileId,
                      status,
                      error,
                    });
                    throw error;
                  } else {
                    // notify only when status changes
                    if (previousStatus !== status) {
                      previousStatus = status;
                      let isLoading = false;
                      let supportingText: string;

                      if (status === IApiFileUploadStatus.UPLOADING) {
                        isLoading = true;
                      } else if (status === IApiFileUploadStatus.ENCODING) {
                        isLoading = true;
                        supportingText = 'Processing file...';
                      } else {
                        processedFilesIds.add(fileId);
                      }

                      filesInfoRef.current = filesInfoRef.current
                        .map((fileInfo) =>
                          fileInfo.id === fileId
                            ? {
                                ...fileInfo,
                                loading: isLoading,
                                progress: undefined,
                                supportingText,
                              }
                            : fileInfo,
                        )
                        .slice(-(fileDropZoneProps.maxFiles ?? 0));
                      setFilesInfo(filesInfoRef.current);

                      onStatusChange({ type: fileType, id: fileId, status });
                    }
                  }

                  if (status !== IApiFileUploadStatus.READY) {
                    // pause
                    await new Promise((resolve) =>
                      setTimeout(resolve, waitTime),
                    );
                    // wait a bit more each time: as processing large files takes more time,
                    // we can check less frequently
                    waitTime = getPollingWaitTime(waitTime);
                  }
                }
              }),
            );

            return Array.from(processedFilesIds);
          })
          .then(() => {
            onChange?.(
              filesInfoRef.current
                .filter(({ id }) => id !== undefined)
                .map((fileInfo) => ({
                  type: fileInfo.mimeType
                    ? getFileTypeFromMimeType(fileInfo.mimeType)
                    : undefined,
                  id: fileInfo.id!,
                }))
                .slice(-(fileDropZoneProps.maxFiles ?? 0)),
            );
          })
          .catch((error: Error) => {
            filesInfoRef.current = filesInfoRef.current
              .map((fileInfo) =>
                acceptedFilesInfo.find(({ key }) => fileInfo.key === key)
                  ? {
                      ...fileInfo,
                      loading: false,
                      stale: true,
                      errorText: error.message,
                      canRetry: true,
                    }
                  : fileInfo,
              )
              .slice(-(fileDropZoneProps.maxFiles ?? 0));
            setFilesInfo(filesInfoRef.current);
            onStatusChange({ error, status: IApiFileUploadStatus.ERROR });
          });
      },
      [
        api,
        appId,
        onChange,
        onStatusChange,
        getPollingWaitTime,
        getFileStatus,
        handleProgressEvent,
        fileDropZoneProps.maxFiles,
      ],
    );

    const handleAcceptedDrop = (
      acceptedFilesInfo: Array<IFileDropZoneFileInfo>,
    ): void => {
      filesInfoRef.current = [
        ...filesInfoRef.current,
        ...acceptedFilesInfo.map((file) => ({
          ...file,
          loading: true,
        })),
      ].slice(-(fileDropZoneProps.maxFiles ?? 0));
      setFilesInfo(filesInfoRef.current);

      void uploadFiles(acceptedFilesInfo);
    };

    const handleRejectedDrop = (
      rejectedFilesInfo: Array<IFileDropZoneFileInfo>,
    ): void => {
      filesInfoRef.current = [
        ...filesInfoRef.current,
        ...rejectedFilesInfo.map((file) => ({
          ...file,
          stale: true,
        })),
      ].slice(-(fileDropZoneProps.maxFiles ?? 0));
      setFilesInfo(filesInfoRef.current);
    };

    const handleRetry = (fileInfo: IFileDropZoneFileInfo) =>
      void uploadFiles([fileInfo]);

    const handleDelete = (fileInfo: IFileDropZonePartialFileInfo): void => {
      fileInfo.abortController?.abort();
      // TODO call endpoint DELETE /files/:id when it is available to clean server resources and bucket object
      filesInfoRef.current = filesInfoRef.current
        .filter(({ key }) => fileInfo.key !== key)
        .slice(-(fileDropZoneProps.maxFiles ?? 0));
      setFilesInfo(filesInfoRef.current);
      onChange?.(
        filesInfoRef.current
          .filter(({ id }) => id !== undefined)
          .map((fileInfo) => ({
            type: fileInfo.mimeType
              ? getFileTypeFromMimeType(fileInfo.mimeType)
              : undefined,
            id: fileInfo.id!,
          }))
          .slice(-(fileDropZoneProps.maxFiles ?? 0)),
      );
    };

    const handleFileSelectedFromGallery = (
      files: Array<IFileUploaderValue>,
    ): void => {
      setFileGalleryDialogOpened(false);

      onChange?.([
        ...(value ?? []),
        ...files.map(({ type, id }) => ({
          type,
          id,
        })),
      ]);
    };

    const accept = fileDropZoneProps.accept ??
      acceptTypes?.reduce((acc, type) => {
        const mimeType = typeToMimeType[type];
        return mimeType ? { ...acc, ...mimeType } : acc;
      }, {}) ?? {
        'image/*': [],
        'video/*': [],
        'application/pdf': ['.pdf'],
      };

    return (
      <>
        {appId && (
          <FileGalleryDialog
            appId={appId}
            opened={fileGalleryDialogOpened}
            onClose={() => setFileGalleryDialogOpened(false)}
            onSelect={handleFileSelectedFromGallery}
            acceptTypes={acceptTypes}
            multi={
              fileDropZoneProps.maxFiles === undefined ||
              fileDropZoneProps.maxFiles > 1
            }
          />
        )}

        <FileDropZone
          {...fileDropZoneProps}
          extraActions={
            appId && (
              <>
                <Button
                  variant="text"
                  disabled={disabled}
                  onClick={() => setFileGalleryDialogOpened(true)}
                  icon={<FontAwesomeIcon icon={faFolderImage} />}
                >
                  {t('FileUploader.browseGallery')}
                </Button>
                {fileDropZoneProps.extraActions}
              </>
            )
          }
          ref={forwardedRef}
          files={filesInfo}
          onAcceptedDrop={handleAcceptedDrop}
          onRejectedDrop={handleRejectedDrop}
          accept={accept}
          onDelete={handleDelete}
          onRetry={handleRetry}
          disabled={disabled}
        />
      </>
    );
  },
);
